import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CreateFacilityBillableRateDto,
  FacilityBillableRateDto,
  FacilityDto,
  PagedResultDto,
  UpdateFacilityBillableRateDto
} from '@umss/shared/data-access';
import { HttpHelper, ScopeOfPracticeEnum } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import {
  CreateFacilityContactDto,
  CreateFacilityDto,
  CreateFacilityPositionDto,
  CreateFacilityRoleDto,
  CreateFacilityScopeDto,
  FacilityContactDto,
  FacilityPositionDto,
  FacilityRoleDto,
  FacilityScopeDto,
  FacilitySearchDto,
  UnifiedShiftDto,
  UnifiedShiftSearchDto,
  UpdateFacilityDto,
  UpdateFacilityPositionDto,
  UpdateFacilityRoleDto,
  UpdateFacilityScopeDto,
  AvailableContractorsFilterDto,
  AvailableContractorsForShiftsResponseDto,
  BulkAssignContractorRequestDto,
  BulkAssignContractorResponseDto
} from '../dtos';

@Injectable({ providedIn: 'root' })
export class FacilityService {
  private readonly base = '/api/facilities';

  constructor(private http: HttpClient) {}

  // ============================================
  // ENDPOINT 1: GET /api/facilities
  // Get all facilities with search and filtering (paged)
  // ============================================
  getAllFacilities(
    searchParams: FacilitySearchDto = {}
  ): Observable<PagedResultDto<FacilityDto>> {
    const params = HttpHelper.toHttpParams(searchParams);

    return this.http.get<PagedResultDto<FacilityDto>>(this.base, { params });
  }

  // ============================================
  // ENDPOINT 2: GET /api/facilities/{id}
  // Get facility by ID
  // ============================================
  getFacilityById(id: string): Observable<FacilityDto> {
    return this.http.get<FacilityDto>(`${this.base}/${id}`);
  }

  // ============================================
  // ENDPOINT 3: POST /api/facilities
  // Create new facility
  // ============================================
  createFacility(body: CreateFacilityDto): Observable<FacilityDto> {
    return this.http.post<FacilityDto>(this.base, body);
  }

  // ============================================
  // ENDPOINT 4: PUT /api/facilities/{id}
  // Update facility
  // ============================================
  updateFacility(id: string, body: UpdateFacilityDto): Observable<FacilityDto> {
    return this.http.put<FacilityDto>(`${this.base}/${id}`, body);
  }

  // ============================================
  // ENDPOINT 5: DELETE /api/facilities/{id}
  // Delete facility
  // ============================================
  deleteFacility(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  // ============================================
  // ENDPOINT 6: GET /api/facilities/{id}/contacts
  // Get facility contacts
  // ============================================
  getFacilityContacts(facilityId: string): Observable<FacilityContactDto[]> {
    return this.http.get<FacilityContactDto[]>(
      `${this.base}/${facilityId}/contacts`
    );
  }

  // ============================================
  // ENDPOINT 7: POST /api/facilities/{id}/contacts
  // Add facility contact
  // ============================================
  addFacilityContact(
    facilityId: string,
    body: CreateFacilityContactDto
  ): Observable<FacilityContactDto> {
    return this.http.post<FacilityContactDto>(
      `${this.base}/${facilityId}/contacts`,
      body
    );
  }

  /**
   * @deprecated
   * Use FacilityBillableRatesService
   */
  getFacilityBillableRates(
    facilityId: string
  ): Observable<FacilityBillableRateDto[]> {
    return this.http.get<FacilityBillableRateDto[]>(
      `${this.base}/${facilityId}/billable-rates`
    );
  }

  /**
   * @deprecated
   * Use FacilityBillableRatesService
   */
  createFacilityBillableRate(
    facilityId: string,
    body: CreateFacilityBillableRateDto
  ): Observable<FacilityBillableRateDto> {
    return this.http.post<FacilityBillableRateDto>(
      `${this.base}/${facilityId}/billable-rates`,
      body
    );
  }
  /**
   * @deprecated
   * Use FacilityBillableRatesService
   */
  updateFacilityBillableRate(
    facilityId: string,
    rateId: string,
    body: UpdateFacilityBillableRateDto
  ): Observable<FacilityBillableRateDto> {
    return this.http.put<FacilityBillableRateDto>(
      `${this.base}/${facilityId}/billable-rates/${rateId}`,
      body
    );
  }

  /**
   * @deprecated
   * Use FacilityBillableRatesService
   */
  getCurrentRateByScope(
    facilityId: string,
    scope: ScopeOfPracticeEnum
  ): Observable<number | null> {
    return this.http.get<number | null>(
      `${this.base}/${facilityId}/billable-rates/${scope}`
    );
  }

  /**
   * @deprecated
   * Use FacilityBillableRatesService
   */
  deleteFacilityBillableRate(
    facilityId: string,
    rateId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/${facilityId}/billable-rates/${rateId}`
    );
  }

  // ============================================
  // ENDPOINT 15: GET /api/facilities/{id}/scopes
  // Get all scopes for a facility
  // ============================================
  getFacilityScopes(facilityId: string): Observable<FacilityScopeDto[]> {
    return this.http.get<FacilityScopeDto[]>(
      `${this.base}/${facilityId}/scopes`
    );
  }

  // ============================================
  // ENDPOINT 16: POST /api/facilities/{id}/scopes
  // Add a scope to a facility
  // ============================================
  addFacilityScope(
    facilityId: string,
    body: CreateFacilityScopeDto
  ): Observable<FacilityScopeDto> {
    return this.http.post<FacilityScopeDto>(
      `${this.base}/${facilityId}/scopes`,
      body
    );
  }

  // ============================================
  // ENDPOINT 17: PUT /api/facilities/{id}/scopes/{scopeId}
  // Update a facility scope
  // ============================================
  updateFacilityScope(
    facilityId: string,
    scopeId: number,
    body: UpdateFacilityScopeDto
  ): Observable<FacilityScopeDto> {
    return this.http.put<FacilityScopeDto>(
      `${this.base}/${facilityId}/scopes/${scopeId}`,
      body
    );
  }

  // ============================================
  // ENDPOINT 18: DELETE /api/facilities/{id}/scopes/{scopeId}
  // Delete a facility scope
  // ============================================
  deleteFacilityScope(facilityId: string, scopeId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/${facilityId}/scopes/${scopeId}`
    );
  }

  // ============================================
  // ENDPOINT 19: GET /api/facilities/{id}/roles
  // Get all roles for a facility
  // ============================================
  getFacilityRoles(facilityId: string): Observable<FacilityRoleDto[]> {
    return this.http.get<FacilityRoleDto[]>(`${this.base}/${facilityId}/roles`);
  }

  // ============================================
  // ENDPOINT 20: GET /api/facilities/{id}/roles/{roleId}
  // Get a specific role by ID
  // ============================================
  getFacilityRoleById(
    facilityId: string,
    roleId: number
  ): Observable<FacilityRoleDto> {
    return this.http.get<FacilityRoleDto>(
      `${this.base}/${facilityId}/roles/${roleId}`
    );
  }

  // ============================================
  // ENDPOINT 21: POST /api/facilities/{id}/roles
  // Create a new role for a facility
  // ============================================
  createFacilityRole(
    facilityId: string,
    body: CreateFacilityRoleDto
  ): Observable<FacilityRoleDto> {
    return this.http.post<FacilityRoleDto>(
      `${this.base}/${facilityId}/roles`,
      body
    );
  }

  // ============================================
  // ENDPOINT 22: PUT /api/facilities/{id}/roles/{roleId}
  // Update a facility role
  // ============================================
  updateFacilityRole(
    facilityId: string,
    roleId: number,
    body: UpdateFacilityRoleDto
  ): Observable<FacilityRoleDto> {
    return this.http.put<FacilityRoleDto>(
      `${this.base}/${facilityId}/roles/${roleId}`,
      body
    );
  }

  // ============================================
  // ENDPOINT 23: DELETE /api/facilities/{id}/roles/{roleId}
  // Delete a facility role
  // ============================================
  deleteFacilityRole(facilityId: string, roleId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${facilityId}/roles/${roleId}`);
  }

  // ============================================
  // ENDPOINT 24: GET /api/facilities/{id}/positions
  // Get all positions for a facility
  // ============================================
  getFacilityPositions(facilityId: string): Observable<FacilityPositionDto[]> {
    return this.http.get<FacilityPositionDto[]>(
      `${this.base}/${facilityId}/positions`
    );
  }

  // ============================================
  // ENDPOINT 25: GET /api/facilities/{id}/positions/{positionId}
  // Get a specific position by ID
  // ============================================
  getFacilityPositionById(
    facilityId: string,
    positionId: number
  ): Observable<FacilityPositionDto> {
    return this.http.get<FacilityPositionDto>(
      `${this.base}/${facilityId}/positions/${positionId}`
    );
  }

  // ============================================
  // ENDPOINT 26: POST /api/facilities/{id}/positions
  // Create a new position for a facility
  // ============================================
  createFacilityPosition(
    facilityId: string,
    body: CreateFacilityPositionDto
  ): Observable<FacilityPositionDto> {
    return this.http.post<FacilityPositionDto>(
      `${this.base}/${facilityId}/positions`,
      body
    );
  }

  // ============================================
  // ENDPOINT 27: PUT /api/facilities/{id}/positions/{positionId}
  // Update a facility position
  // ============================================
  updateFacilityPosition(
    facilityId: string,
    positionId: number,
    body: UpdateFacilityPositionDto
  ): Observable<FacilityPositionDto> {
    return this.http.put<FacilityPositionDto>(
      `${this.base}/${facilityId}/positions/${positionId}`,
      body
    );
  }

  /**
   * @deprecated
   * Use FacilityPositionsService
   */
  deleteFacilityPosition(
    facilityId: string,
    positionId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/${facilityId}/positions/${positionId}`
    );
  }

  // ============================================
  // ENDPOINT: GET /api/facilities/{id}/all-shifts
  // Get all shifts and shift needs for facility with filtering and pagination
  // ============================================
  /**
   * Get all shifts and shift needs for a facility with filtering and pagination.
   * Returns a unified view of assigned shifts and open shift needs.
   *
   * @param facilityId - ID of the facility
   * @param searchParams - Search and filter parameters
   * @returns Observable of paged result containing unified shift DTOs
   */
  getAllShiftsForFacility(
    facilityId: string,
    searchParams: UnifiedShiftSearchDto = {}
  ): Observable<PagedResultDto<UnifiedShiftDto>> {
    const params = HttpHelper.toHttpParams(searchParams);

    return this.http.get<PagedResultDto<UnifiedShiftDto>>(
      `${this.base}/${facilityId}/all-shifts`,
      { params }
    );
  }

  // ============================================
  // ENDPOINT: GET /api/facilities/{id}/shifts/available-contractors
  // Get available contractors for a batch of shifts
  // ============================================
  /**
   * Get contractors for the Assign Contractor modal. Returns every active contractor of the
   * shift needs' (single) scope of practice, each tagged with an availability status, paged
   * and filtered. All selected shift needs must share one scope of practice.
   *
   * @param facilityId - ID of the facility
   * @param filter - Shift need IDs plus filter/paging options
   * @returns Observable containing the paged available-contractors response
   */
  getAvailableContractorsForShifts(
    facilityId: string,
    filter: AvailableContractorsFilterDto
  ): Observable<AvailableContractorsForShiftsResponseDto> {
    const params = HttpHelper.toHttpParams(filter);

    return this.http.get<AvailableContractorsForShiftsResponseDto>(
      `${this.base}/${facilityId}/shifts/available-contractors`,
      { params }
    );
  }

  // ============================================
  // ENDPOINT: POST /api/facilities/{id}/shifts/assign
  // Bulk assign contractor to multiple shifts
  // ============================================
  /**
   * Creates pending ShiftRequests for a contractor to work multiple shifts.
   * The contractor must accept each invitation.
   *
   * @param facilityId - ID of the facility
   * @param body - Bulk assignment request data
   * @returns Observable containing bulk assignment response with results for each shift
   */
  bulkAssignContractorToShifts(
    facilityId: string,
    body: BulkAssignContractorRequestDto
  ): Observable<BulkAssignContractorResponseDto> {
    return this.http.post<BulkAssignContractorResponseDto>(
      `${this.base}/${facilityId}/shifts/assign`,
      body
    );
  }
}
